/**
 * telemetryStore — real-time data layer for the Live Monitor page.
 *
 * Streaming samples deliberately BYPASS Redux and React state. Each card
 * subscribes to exactly one device and receives batches directly, so a
 * 20-device feed never re-renders the page tree.
 *
 * Live mode (default): one socket.io connection to `liveApi` (the :3002
 * server running routes/api/live.js). watchPatient() emits 'live:watch';
 * the server bridges the device's TCP stream into 'live:data' batches.
 *
 * Demo mode (open /live-monitor?demo=1): synthetic patients with scripted
 * scenarios, no backend needed. Useful for UI work.
 */
import socketIOClient from 'socket.io-client';
import axios from 'axios';
import { liveApi } from '../../config/config';

// ---------------------------------------------------------------------------
// The parameters shown on every card.
// warn/crit are [low, high] bands; outside warn => warning, outside crit => critical.
// ---------------------------------------------------------------------------
export const PARAMS = [
  { key: 'hr',   label: 'Heart Rate', unit: 'bpm', digits: 0, warn: [50, 120],  crit: [40, 150],  hero: true },
  { key: 'spo2', label: 'SpO2',       unit: '%',   digits: 0, warn: [92, 101],  crit: [88, 101] },
  { key: 'resp', label: 'Resp',       unit: 'rpm', digits: 0, warn: [10, 24],   crit: [6, 30] },
  { key: 'temp', label: 'Temp',       unit: '°C',  digits: 1, warn: [35.5, 38], crit: [34, 39.5] },
];

// Batteries ride as small corner badges, not tiles: the ECG device's and the
// phone's sit on the Heart Rate section, the SpO2 sensor's on the SpO2 tile.
// Same [low, high] band shape so paramStatus() applies (<20% warn, <10% crit).
export const BATTERIES = {
  ecgBatt:    { key: 'ecgBatt',    label: 'ECG battery',         warn: [20, 101], crit: [10, 101] },
  spo2Batt:   { key: 'spo2Batt',   label: 'SpO2 sensor battery', warn: [20, 101], crit: [10, 101] },
  mobileBatt: { key: 'mobileBatt', label: 'Phone battery',       warn: [20, 101], crit: [10, 101] },
};

export function paramStatus(param, value) {
  if (value == null || isNaN(value)) return 'stale';
  if (value < param.crit[0] || value > param.crit[1]) return 'critical';
  if (value < param.warn[0] || value > param.warn[1]) return 'warning';
  return 'normal';
}

// Sort order on the board: active patients first (by severity), OFFLINE last —
// so a disconnecting card drifts to the bottom instead of jumping into the
// middle of the active list.
export const SEVERITY_RANK = { critical: 0, warning: 1, normal: 2, stale: 3 };

// A gap longer than this since the last sample means the device was
// disconnected → wipe the stale trace so resumed data plots fresh.
const RECONNECT_GAP_MS = 4000;

// Parameters offered on the 24h trends chart (dark-surface categorical colors).
export const TREND_PARAMS = [
  { key: 'hr',   label: 'HR',   unit: 'bpm', digits: 0, color: '#3987e5' },
  { key: 'spo2', label: 'SpO2', unit: '%',   digits: 0, color: '#d95926' },
  { key: 'resp', label: 'Resp', unit: 'rpm', digits: 0, color: '#199e70' },
  { key: 'temp', label: 'Temp', unit: '°C',  digits: 1, color: '#c98500' },
];

export const isDemo = () =>
  typeof window !== 'undefined' && /[?&]demo=1/.test(window.location.search);

// ---------------------------------------------------------------------------
// Ring buffers — fixed memory per device, no allocation while streaming.
// ---------------------------------------------------------------------------
const WINDOW_SECONDS = 60;
const MAX_HZ = 256;                        // real stream is 250 Hz
const CAPACITY = WINDOW_SECONDS * MAX_HZ;  // 60s of headroom at up to 256 Hz

const buffers = new Map();   // deviceId -> ring buffer
const latest = new Map();    // deviceId -> { vitals, t }
const listeners = new Map(); // deviceId -> Set<fn>
const bufferGen = new Map(); // deviceId -> generation (bumped on a lead-on wipe)
const leadState = new Map(); // deviceId -> last leadOff (for edge detection)
const pastBatches = new Map(); // deviceId -> consecutive batches lying entirely in the past
const PAST_RESET_BATCHES = 6;  // ~3 s at the 500 ms batch cadence → treat as a device clock step-back

function makeBuffer() {
  return {
    times: new Float64Array(CAPACITY),
    values: new Float32Array(CAPACITY),
    head: 0,
    size: 0,
    cap: CAPACITY,
  };
}

export function pushBatch(deviceId, batch) {
  let buf = buffers.get(deviceId);
  if (!buf) { buf = makeBuffer(); buffers.set(deviceId, buf); }

  // Lead-on refresh: when electrodes reconnect (leadOff true -> false), wipe the
  // stale trace BEFORE appending the fresh post-reconnect samples, so the graph
  // restarts clean instead of scrolling the old/flatline data.
  const nowLead = !!(batch.vitals && batch.vitals.leadOff);
  const prevLead = leadState.get(deviceId);
  if (prevLead === true && nowLead === false) {
    buf.head = 0;
    buf.size = 0;
    bufferGen.set(deviceId, (bufferGen.get(deviceId) || 0) + 1);
  }
  leadState.set(deviceId, nowLead);

  let wave = batch.wave || [];
  // Reconnect refresh: a big time gap since the last sample means the device was
  // disconnected — wipe the stale trace so the resumed data plots fresh instead
  // of being joined to the frozen old graph by a bridging line.
  if (buf.size && wave.length) {
    const newestExisting = buf.times[(buf.head - 1 + buf.cap) % buf.cap];
    if (wave[0].t - newestExisting > RECONNECT_GAP_MS) {
      buf.head = 0;
      buf.size = 0;
      bufferGen.set(deviceId, (bufferGen.get(deviceId) || 0) + 1);
      pastBatches.set(deviceId, 0);
    } else if (wave[wave.length - 1].t <= newestExisting) {
      // Entire batch lies in the past. Once or twice that is a store-and-forward
      // replay of samples we already have — skip it so the trace can't jump
      // back. But if it KEEPS happening, the phone's clock was stepped back
      // (NTP correction, manual change): every new sample would be discarded
      // and the trace would freeze while the card still says "live". After
      // PAST_RESET_BATCHES in a row the new timeline wins: wipe and restart.
      const n = (pastBatches.get(deviceId) || 0) + 1;
      if (n >= PAST_RESET_BATCHES) {
        buf.head = 0;
        buf.size = 0;
        bufferGen.set(deviceId, (bufferGen.get(deviceId) || 0) + 1);
        pastBatches.set(deviceId, 0);
      } else {
        pastBatches.set(deviceId, n);
        wave = [];
      }
    } else if (wave[0].t <= newestExisting) {
      pastBatches.set(deviceId, 0);
      // Batch partially overlaps what's buffered — keep only the new tail.
      wave = wave.filter(s => s.t > newestExisting);
    }
  }
  if (wave.length) pastBatches.set(deviceId, 0); // real new samples → not a clock step-back
  for (let i = 0; i < wave.length; i++) {
    buf.times[buf.head] = wave[i].t;
    buf.values[buf.head] = wave[i].v;
    buf.head = (buf.head + 1) % buf.cap;
    if (buf.size < buf.cap) buf.size++;
  }
  const vitals = Object.assign({}, batch.vitals);
  // Streams that report a single unnamed battery mean the ECG device's.
  if (vitals.batt != null && vitals.ecgBatt == null) vitals.ecgBatt = vitals.batt;
  if (vitals.phoneBatt != null && vitals.mobileBatt == null) vitals.mobileBatt = vitals.phoneBatt;
  // Streams that carry only a raw waveform get a client-side HR estimate.
  if (vitals.hr == null && wave.length) {
    // Window the estimate by the DATA's own clock (newest sample), never
    // batch.t: that is the server's clock, the buffer holds the phone's. A
    // phone more than 8 s behind the server made every sample look "too old"
    // and the heart rate showed "--" on a perfectly good trace.
    const est = estimateHr(buf, buf.times[(buf.head - 1 + buf.cap) % buf.cap]);
    if (est != null) { vitals.hr = est; vitals.hrEstimated = true; }
  }
  latest.set(deviceId, { vitals: vitals, t: batch.t });
  accumulateTrend(deviceId, vitals, batch.t);
  const subs = listeners.get(deviceId);
  if (subs) subs.forEach(fn => fn({ ...batch, vitals }));
}

export function subscribe(deviceId, fn) {
  let subs = listeners.get(deviceId);
  if (!subs) { subs = new Set(); listeners.set(deviceId, subs); }
  subs.add(fn);
  return () => subs.delete(fn);
}

export function getBuffer(deviceId) { return buffers.get(deviceId); }
export function getLatest(deviceId) { return latest.get(deviceId); }

// Generation counter — changes when the buffer is wiped (lead-on refresh), so
// the renderer can reset its autoscale/playback clock.
export function getBufferGen(deviceId) { return bufferGen.get(deviceId) || 0; }

// Timestamp of the newest sample in the ring buffer (the render clock chases
// this in data-time, which is robust to device/server/client clock differences).
export function getLatestSampleTime(deviceId) {
  const buf = buffers.get(deviceId);
  if (!buf || !buf.size) return null;
  return buf.times[(buf.head - 1 + buf.cap) % buf.cap];
}

// Timestamp of the oldest sample still in the buffer — used to measure how much
// data has been buffered during pre-roll.
export function getOldestSampleTime(deviceId) {
  const buf = buffers.get(deviceId);
  if (!buf || !buf.size) return null;
  return buf.times[(buf.head - buf.size + buf.cap) % buf.cap];
}

// ---------------------------------------------------------------------------
// 24h trends — minute-averaged history per parameter, accumulated while the
// page watches the device. (True backfill for hours before the page opened
// needs a history endpoint on the backend — pending with the live bridge.)
// ---------------------------------------------------------------------------
const TREND_MINUTES = 24 * 60;
const TREND_KEYS = TREND_PARAMS.map(p => p.key);
const trends = new Map(); // deviceId -> { times:[], series:{key:[]}, acc }

function trendOf(deviceId) {
  let tr = trends.get(deviceId);
  if (!tr) {
    tr = { times: [], series: {}, acc: null };
    TREND_KEYS.forEach(k => { tr.series[k] = []; });
    trends.set(deviceId, tr);
  }
  return tr;
}

function pushTrendPoint(tr, timeMs, values) {
  tr.times.push(timeMs);
  TREND_KEYS.forEach(k => tr.series[k].push(values[k] != null && !isNaN(values[k]) ? values[k] : null));
  if (tr.times.length > TREND_MINUTES) {
    tr.times.shift();
    TREND_KEYS.forEach(k => tr.series[k].shift());
  }
}

function accumulateTrend(deviceId, vitals, t) {
  const tr = trendOf(deviceId);
  const minute = Math.floor(t / 60000);
  if (!tr.acc || tr.acc.minute !== minute) {
    if (tr.acc) {
      const avg = {};
      TREND_KEYS.forEach(k => {
        avg[k] = tr.acc.counts[k] ? tr.acc.sums[k] / tr.acc.counts[k] : null;
      });
      pushTrendPoint(tr, tr.acc.minute * 60000 + 30000, avg);
    }
    tr.acc = { minute: minute, sums: {}, counts: {} };
  }
  TREND_KEYS.forEach(k => {
    const v = vitals[k];
    if (v != null && !isNaN(v)) {
      tr.acc.sums[k] = (tr.acc.sums[k] || 0) + v;
      tr.acc.counts[k] = (tr.acc.counts[k] || 0) + 1;
    }
  });
}

/** Stored minute points plus the current in-progress minute as a provisional tail. */
export function getTrend(deviceId) {
  const tr = trends.get(deviceId);
  if (!tr) return { times: [], series: Object.fromEntries(TREND_KEYS.map(k => [k, []])) };
  const out = { times: tr.times.slice(), series: {} };
  TREND_KEYS.forEach(k => { out.series[k] = tr.series[k].slice(); });
  if (tr.acc) {
    out.times.push(tr.acc.minute * 60000 + 30000);
    TREND_KEYS.forEach(k => {
      out.series[k].push(tr.acc.counts[k] ? tr.acc.sums[k] / tr.acc.counts[k] : null);
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Crude R-peak rate estimate over the last 8s: adaptive threshold, 250ms
// refractory. Good enough for a card readout; the tile is tagged "est."
// ---------------------------------------------------------------------------
/**
 * Heart rate from RAW ECG — the same slope-energy detector the server uses
 * (web_server/lib/liveAlarmEngine.js estimateHrFrom; keep the two identical).
 * The server normally publishes the rate (vitals.hr + hrEstimated), so this
 * runs only against an older backend that doesn't.
 *
 * The earlier method (count crossings of a level 60 % between min and max) was
 * only right on a clean flat trace: with baseline wander a true 72 bpm read 23,
 * with tall T waves 135, and one motion spike blanked it.
 *
 *   slope over ~8 ms, squared → 120 ms moving sum → threshold at 30 % of the
 *   window's 95th percentile → peaks ≥ 250 ms apart → rate from the MEDIAN
 *   beat interval. No beat-like contrast (noise / flat line) → no rate.
 */
function estimateHr(buf, now) {
  const SPAN = 8000;
  const start = (buf.head - buf.size + buf.cap) % buf.cap;
  const times = [], values = [];
  for (let i = 0; i < buf.size; i++) {
    const idx = (start + i) % buf.cap;
    if (buf.times[idx] < now - SPAN) continue;
    times.push(buf.times[idx]); values.push(buf.values[idx]);
  }
  const m = times.length;
  if (m < 64) return null;
  const span = times[m - 1] - times[0];
  if (span < 3000) return null;
  const dt = span / (m - 1);
  const k = Math.max(1, Math.round(8 / dt));
  const w = Math.max(2, Math.round(120 / dt));
  const mwi = new Float64Array(m), e = new Float64Array(m);
  let run = 0;
  for (let i = 0; i < m; i++) {
    if (i >= k) { const d = values[i] - values[i - k]; e[i] = d * d; }
    run += e[i];
    if (i >= w) run -= e[i - w];
    mwi[i] = run;
  }
  const sorted = Array.prototype.slice.call(mwi).sort((a, b) => a - b);
  const p95 = sorted[Math.floor(m * 0.95)];
  const med = sorted[Math.floor(m * 0.5)];
  if (!(p95 > 0) || p95 < 6 * (med + 1e-9)) return null;   // no beat-like contrast
  const thr = 0.3 * p95;
  const peaks = [];
  let lastV = 0;
  for (let i = 1; i < m - 1; i++) {
    const v = mwi[i];
    if (v <= thr || v < mwi[i - 1] || v <= mwi[i + 1]) continue;
    if (peaks.length && times[i] - peaks[peaks.length - 1] < 250) {
      if (v > lastV) { peaks[peaks.length - 1] = times[i]; lastV = v; }
      continue;
    }
    peaks.push(times[i]); lastV = v;
  }
  if (peaks.length < 3) return null;
  const rr = [];
  for (let i = 1; i < peaks.length; i++) rr.push(peaks[i] - peaks[i - 1]);
  rr.sort((a, b) => a - b);
  const mid = rr.length >> 1;
  const medRr = rr.length % 2 ? rr[mid] : (rr[mid - 1] + rr[mid]) / 2;
  const hr = 60000 / medRr;
  return hr >= 20 && hr <= 300 ? hr : null;
}

// ---------------------------------------------------------------------------
// LIVE mode — socket.io to the /api/live bridge.
// ---------------------------------------------------------------------------
let socket = null;
const watched = new Map(); // deviceId -> patientId

// The DASHBOARD's own link to the server. When this drops, every card goes
// silent at once — that is "this browser lost the server", not "every
// patient's device disconnected", and must not raise per-patient alarms.
let serverUp = true;               // optimistic until a disconnect is seen
let serverUpSince = Date.now();    // cards ignore silence that predates this (+ grace)
const serverListeners = new Set();
function setServerUp(up) {
  if (serverUp === up) return;
  serverUp = up;
  if (up) serverUpSince = Date.now();
  serverListeners.forEach(fn => fn(up));
}
export function isServerUp() { return isDemo() ? true : serverUp; }
export function getServerUpSince() { return serverUpSince; }
export function onServerLink(fn) { serverListeners.add(fn); return () => serverListeners.delete(fn); }

function getSocket() {
  if (!socket) {
    socket = socketIOClient(liveApi);
    socket.on('connect', () => setServerUp(true));
    socket.on('disconnect', () => setServerUp(false));
    socket.on('connect_error', () => setServerUp(false));
    socket.on('live:data', msg => {
      if (msg && msg.deviceId) pushBatch(String(msg.deviceId), msg);
    });
    // socket.io re-subscribes rooms are server-side state — rejoin on reconnect
    socket.on('reconnect', () => {
      watched.forEach((patientId, deviceId) =>
        socket.emit('live:watch', { deviceId, patientId }));
    });
  }
  return socket;
}

export function watchPatient(deviceId, patientId) {
  if (isDemo()) { demoWatch(deviceId); return; }
  watched.set(deviceId, patientId || '');
  getSocket().emit('live:watch', { deviceId, patientId: patientId || '' });
  backfillTrend(deviceId);
}

// Pull the last 24h of minute averages from the server so the trends chart
// starts full instead of only accumulating from page-open.
const backfilled = new Set();
function backfillTrend(deviceId) {
  if (backfilled.has(deviceId)) return;
  backfilled.add(deviceId);
  axios.get(`${liveApi}api/live/trends/${deviceId}`)
    .then(res => {
      const rows = Array.isArray(res.data) ? res.data : [];
      if (!rows.length) return;
      const tr = trendOf(deviceId);
      // Prepend server history older than anything collected locally.
      const cutoff = tr.times.length ? tr.times[0] : Infinity;
      const older = rows.filter(r => r.t < cutoff);
      if (!older.length) return;
      tr.times = older.map(r => r.t).concat(tr.times);
      TREND_KEYS.forEach(k => {
        tr.series[k] = older.map(r => (r[k] != null ? r[k] : null)).concat(tr.series[k]);
      });
      while (tr.times.length > TREND_MINUTES) {
        tr.times.shift();
        TREND_KEYS.forEach(k => tr.series[k].shift());
      }
    })
    .catch(() => backfilled.delete(deviceId)); // retry on next watch
}

export function unwatchPatient(deviceId) {
  if (isDemo()) return;
  watched.delete(deviceId);
  if (socket) socket.emit('live:unwatch', { deviceId });
}

export function disconnectLive() {
  if (socket) { socket.disconnect(); socket = null; }
  watched.clear();
  demoStop();
}

// ---------------------------------------------------------------------------
// DEMO mode — synthetic ECG + drifting vitals with scripted scenarios so
// every card state is visible (?demo=1). No backend required.
// ---------------------------------------------------------------------------
export const DEMO_PATIENTS = [
  { deviceId: 'DM-1041', patientId: 'demo1', name: 'Anil Kumar (demo)',    scenario: 'vt', ward: 'ICU-2', bed: '04' },      // periodic VT episodes
  { deviceId: 'DM-1042', patientId: 'demo2', name: 'Meera Nair (demo)',    scenario: 'tachy' },   // HIGH HR + low phone battery
  { deviceId: 'DM-1043', patientId: 'demo3', name: 'Joseph Mathew (demo)', scenario: 'lowspo2' }, // low SpO2 + SpO2 sensor battery
  { deviceId: 'DM-1044', patientId: 'demo4', name: 'Sara Thomas (demo)',   scenario: 'lowbatt', ward: 'Cardiac Ward B', bed: '17' }, // low ECG battery + lead-off episodes
  { deviceId: 'DM-1045', patientId: 'demo5', name: 'George Kurian (demo)', scenario: 'stale', ward: 'ICU-2', bed: '09' },   // goes offline
  // ECG sensor switched off after 12 s while the phone app stays alive: from then
  // on only idle heartbeats arrive, 5-6.6 s apart like the real app over a mobile
  // network. At 75 s the phone goes too. Exercises DEVICE OFF -> DISCONNECTED.
  { deviceId: 'DM-1046', patientId: 'demo6', name: 'Latha Menon (demo)', scenario: 'deviceoff', ward: 'CCU', bed: '03' },
];

const DEMO_HZ = 16, DEMO_BATCH_MS = 500;
let demoTimer = null;
const demoSims = new Map();

function demoWatch(deviceId) {
  const p = DEMO_PATIENTS.find(d => d.deviceId === deviceId);
  if (!p || demoSims.has(deviceId)) return;
  demoSims.set(deviceId, {
    scenario: p.scenario,
    hr: 72 + Math.random() * 10, spo2: 97 + Math.random() * 2,
    resp: 14 + Math.random() * 4, temp: 36.6 + Math.random() * 0.4,
    ecgBatt: p.scenario === 'lowbatt' ? 14 : 55 + Math.random() * 40,
    spo2Batt: p.scenario === 'lowspo2' ? 16 : 45 + Math.random() * 50,
    mobileBatt: p.scenario === 'tachy' ? 16 : 40 + Math.random() * 55,
    phase: Math.random(), elapsed: 0,
  });
  seedDemoTrend(deviceId, p.scenario);
  if (!demoTimer) demoTimer = setInterval(demoTick, DEMO_BATCH_MS);
}

// Pre-fill 24h of minute-averaged history (circadian drift + noise, plus a
// few scenario-flavoured episodes) so the trends chart is populated at once.
function seedDemoTrend(deviceId, scenario) {
  const tr = trendOf(deviceId);
  if (tr.times.length) return;
  const now = Date.now();
  for (let m = TREND_MINUTES; m > 0; m--) {
    const t = now - m * 60000;
    const dayFrac = ((t / 3600000) % 24) / 24;
    const circ = Math.sin(2 * Math.PI * (dayFrac - 0.3));
    let hr = 72 + 9 * circ + (Math.random() - 0.5) * 5;
    if (scenario === 'tachy' && (m % 240) < 25) hr = 125 + Math.random() * 25;  // episodes
    if (scenario === 'vt' && (m % 300) < 8) hr = 165 + Math.random() * 20;
    pushTrendPoint(tr, t, {
      hr: hr,
      spo2: Math.min(100, (scenario === 'lowspo2' ? 93.5 : 97.3) + circ * 0.7 + (Math.random() - 0.5) * 0.8),
      resp: 15 + 2.5 * circ + (Math.random() - 0.5) * 1.5,
      temp: 36.6 + 0.35 * Math.sin(2 * Math.PI * (dayFrac - 0.6)) + (Math.random() - 0.5) * 0.08,
    });
  }
}

function demoStop() {
  clearInterval(demoTimer);
  demoTimer = null;
  demoSims.clear();
}

function ecgSample(f) {
  const g = (c, wdt) => Math.exp(-Math.pow((f - c) / wdt, 2));
  return g(0.12, 0.018) - 0.18 * g(0.09, 0.012) - 0.22 * g(0.15, 0.014)
    + 0.28 * g(0.38, 0.05) + (Math.random() - 0.5) * 0.03;
}

function demoTick() {
  const now = Date.now();
  demoSims.forEach((s, deviceId) => {
    s.elapsed += DEMO_BATCH_MS / 1000;
    if (s.scenario === 'stale' && s.elapsed > 15) return; // goes silent
    if (s.scenario === 'deviceoff' && s.elapsed > 12) {
      if (s.elapsed > 75) return;                          // phone gone as well
      if (s.nextHb == null) s.nextHb = s.elapsed;
      if (s.elapsed < s.nextHb) return;
      s.nextHb = s.elapsed + 5 + Math.random() * 1.6;      // jittery idle heartbeat
      pushBatch(deviceId, {                                // what the server emits for an hb: no wave
        t: now, wave: [],
        vitals: { spo2: Math.min(100, s.spo2), ecgBatt: s.ecgBatt, mobileBatt: s.mobileBatt, leadOff: false, events: [] },
      });
      return;
    }

    // Episodes: VT runs 8s out of every 45s; lead-off 6s out of every 35s.
    const inVt = s.scenario === 'vt' && (s.elapsed % 45) > 12 && (s.elapsed % 45) < 20;
    const leadOff = s.scenario === 'lowbatt' && (s.elapsed % 40) > 10 && (s.elapsed % 40) < 25;

    let targetHr = 74;
    if (s.scenario === 'tachy') {
      const c = (s.elapsed % 60) / 60;
      targetHr = c < 0.5 ? 74 + c * 2 * 82 : 156 - (c - 0.5) * 2 * 82;
    }
    if (inVt) targetHr = 185;
    s.hr += (targetHr - s.hr) * (inVt ? 0.25 : 0.05) + (Math.random() - 0.5) * 1.5;
    s.spo2 += ((s.scenario === 'lowspo2' ? 90.5 : 97.5) - s.spo2) * 0.03 + (Math.random() - 0.5) * 0.3;
    s.resp += (15 - s.resp) * 0.02 + (Math.random() - 0.5) * 0.4;
    s.temp += (36.7 - s.temp) * 0.01 + (Math.random() - 0.5) * 0.02;
    s.ecgBatt = Math.max(0, s.ecgBatt - 0.002);
    s.spo2Batt = Math.max(0, s.spo2Batt - 0.003);
    s.mobileBatt = Math.max(0, s.mobileBatt - 0.004);

    const n = DEMO_HZ * DEMO_BATCH_MS / 1000;
    const wave = [];
    for (let i = 0; i < n; i++) {
      s.phase += (s.hr / 60) / DEMO_HZ;
      let v;
      if (leadOff) {
        v = (Math.random() - 0.5) * 0.04;                       // flatline noise
      } else if (inVt) {
        v = Math.sin(s.phase * 2 * Math.PI) * (0.6 + Math.random() * 0.35); // wide fast complexes
      } else {
        v = ecgSample(s.phase % 1);
      }
      wave.push({ t: now - DEMO_BATCH_MS + ((i + 1) * DEMO_BATCH_MS) / n, v: v });
    }
    pushBatch(deviceId, {
      t: now,
      wave: wave,
      vitals: {
        hr: s.hr, spo2: Math.min(100, s.spo2), resp: s.resp, temp: s.temp,
        ecgBatt: s.ecgBatt, spo2Batt: s.spo2Batt, mobileBatt: s.mobileBatt,
        leadOff: leadOff,
        events: inVt ? ['VT'] : [],
      },
    });
  });
}
